import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart3,
  Building2,
  Mail,
  Phone,
  RefreshCw,
  Search,
  UserRound,
} from "lucide-react";

const DAYS_OPTIONS = [5, 10, 15];
const PLAN_OPTIONS = [
  { value: "ALL", label: "Tutti i piani" },
  { value: "FREE", label: "Norma Free" },
  { value: "SYNCRO", label: "Norma Start" },
  { value: "IOT_GUARDIAN", label: "Norma Pro" },
  { value: "CUBO_PREDICTIVE", label: "Norma Enterprise" },
];

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function planBadgeClass(plan) {
  if (plan === "FREE") return "bg-slate-100 text-slate-700";
  if (plan === "SYNCRO") return "bg-sky-100 text-sky-700";
  if (plan === "IOT_GUARDIAN") return "bg-indigo-100 text-indigo-700";
  if (plan === "CUBO_PREDICTIVE") return "bg-emerald-100 text-emerald-700";
  return "bg-slate-100 text-slate-700";
}

function apiError(data, fallback) {
  return data?.message || data?.error || fallback;
}

export default function AziendeStatistiche({ isSuper }) {
  const [days, setDays] = useState(10);
  const [plan, setPlan] = useState("ALL");
  const [query, setQuery] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function loadStats() {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      params.set("days", String(days));
      params.set("plan", plan);
      const res = await fetch(`/api/admin/aziende_stats.php?${params.toString()}`, {
        credentials: "include",
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(apiError(json, "Errore caricamento statistiche aziende"));
      }
      setData(json);
    } catch (err) {
      setError(err.message || "Errore caricamento statistiche aziende");
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isSuper) loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuper, days, plan]);

  const companies = useMemo(() => {
    const rows = Array.isArray(data?.companies) ? data.companies : [];
    const needle = query.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((item) => {
      const haystack = [
        item.nome,
        item.indirizzo,
        item.partita_iva,
        item.referente_nome,
        item.referente_email,
        item.referente_cell,
        item.admin_contacts,
        item.current_plan_label,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [data, query]);

  const followups = Array.isArray(data?.followups) ? data.followups : [];
  const windows = data?.summary?.windows || {};
  const planCounts = Array.isArray(data?.plan_counts) ? data.plan_counts : [];

  if (!isSuper) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600">
        Solo il superadmin puo vedere le statistiche delle aziende.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
              <BarChart3 size={22} />
              Statistiche iscrizioni aziende
            </div>
            <div className="text-sm text-slate-500">
              Conteggi, piani attivi e contatti per follow-up commerciale.
            </div>
          </div>
          <button
            type="button"
            onClick={loadStats}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Aggiorna
          </button>
        </div>

        {error ? (
          <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        ) : null}

        <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-4">
          {DAYS_OPTIONS.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setDays(item)}
              className={[
                "rounded-2xl border p-4 text-left shadow-sm transition",
                days === item
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50",
              ].join(" ")}
            >
              <div className={days === item ? "text-sm text-white/70" : "text-sm text-slate-500"}>
                Ultimi {item} giorni
              </div>
              <div className="mt-1 text-3xl font-semibold">{windows[String(item)] ?? 0}</div>
            </button>
          ))}
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900 shadow-sm">
            <div className="text-sm text-amber-700">Pro diventate Free</div>
            <div className="mt-1 text-3xl font-semibold">{data?.summary?.followup_total ?? 0}</div>
          </div>
        </div>
      </div>

      {followups.length > 0 ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 shadow-sm md:p-6">
          <div className="flex items-center gap-2 text-lg font-semibold text-amber-950">
            <Phone size={18} />
            Da ricontattare
          </div>
          <div className="mt-1 text-sm text-amber-800">
            Aziende iscritte da almeno 15 giorni che risultano passate da Norma Pro a Norma Free.
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {followups.map((item) => (
              <div key={item.id} className="rounded-2xl border border-amber-200 bg-white p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold text-slate-900">{item.nome}</div>
                    <div className="text-xs text-slate-500">Iscritta il {formatDate(item.signup_at)}</div>
                  </div>
                  <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">
                    {item.days_since_signup} giorni
                  </span>
                </div>
                <div className="mt-3 grid gap-1 text-sm text-slate-700">
                  {item.referente_nome ? <div>{item.referente_nome}</div> : null}
                  {item.referente_email ? (
                    <a className="inline-flex items-center gap-2 text-indigo-700" href={`mailto:${item.referente_email}`}>
                      <Mail size={14} />
                      {item.referente_email}
                    </a>
                  ) : null}
                  {item.referente_cell ? (
                    <a className="inline-flex items-center gap-2 text-indigo-700" href={`tel:${item.referente_cell}`}>
                      <Phone size={14} />
                      {item.referente_cell}
                    </a>
                  ) : null}
                  {item.admin_contacts ? (
                    <div className="inline-flex items-start gap-2 text-slate-600">
                      <UserRound size={14} className="mt-0.5" />
                      <span>{item.admin_contacts}</span>
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-6">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[180px]">
            <label className="text-xs font-medium text-slate-600">Periodo</label>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:border-slate-500"
            >
              {DAYS_OPTIONS.map((item) => (
                <option key={item} value={item}>
                  Ultimi {item} giorni
                </option>
              ))}
            </select>
          </div>

          <div className="min-w-[210px]">
            <label className="text-xs font-medium text-slate-600">Piano attuale</label>
            <select
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:border-slate-500"
            >
              {PLAN_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>

          <div className="min-w-[260px] flex-1">
            <label className="text-xs font-medium text-slate-600">Cerca azienda o contatto</label>
            <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5">
              <Search size={16} className="text-slate-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Nome, email, telefono, partita IVA..."
                className="w-full outline-none"
              />
            </div>
          </div>
        </div>

        {planCounts.length > 0 ? (
          <div className="mt-4 flex flex-wrap gap-2">
            {planCounts.map((item) => (
              <span
                key={item.plan_code}
                className={`rounded-full px-3 py-1 text-xs font-semibold ${planBadgeClass(item.plan_code)}`}
              >
                {item.plan_label}: {item.total}
              </span>
            ))}
          </div>
        ) : null}

        <div className="mt-5">
          {loading ? (
            <div className="rounded-xl border border-slate-200 p-5 text-slate-500">
              Caricamento statistiche...
            </div>
          ) : companies.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 p-5 text-slate-500">
              Nessuna azienda trovata con questi filtri.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-slate-500">
                    <th className="px-3 py-3">Azienda</th>
                    <th className="px-3 py-3">Iscrizione</th>
                    <th className="px-3 py-3">Piano</th>
                    <th className="px-3 py-3">Contatti</th>
                    <th className="px-3 py-3">Storico</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((item) => (
                    <tr key={item.id} className="border-b border-slate-100 align-top">
                      <td className="px-3 py-3">
                        <div className="inline-flex items-center gap-2 font-semibold text-slate-900">
                          <Building2 size={15} />
                          {item.nome}
                        </div>
                        <div className="text-xs text-slate-500">ID {item.id}</div>
                        {item.indirizzo ? <div className="text-xs text-slate-500">{item.indirizzo}</div> : null}
                        {item.partita_iva ? <div className="text-xs text-slate-500">P.IVA {item.partita_iva}</div> : null}
                      </td>
                      <td className="px-3 py-3">
                        <div className="font-medium text-slate-800">{formatDate(item.signup_at)}</div>
                        <div className="text-xs text-slate-500">{item.days_since_signup} giorni fa</div>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${planBadgeClass(item.current_plan_code)}`}>
                          {item.current_plan_label}
                        </span>
                        {item.needs_followup ? (
                          <div className="mt-2 rounded-lg bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">
                            Ricontattare
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-3">
                        <div className="grid gap-1 text-slate-700">
                          {item.referente_nome ? <div>{item.referente_nome}</div> : null}
                          {item.referente_email ? (
                            <a className="inline-flex items-center gap-2 text-indigo-700" href={`mailto:${item.referente_email}`}>
                              <Mail size={14} />
                              {item.referente_email}
                            </a>
                          ) : null}
                          {item.referente_cell ? (
                            <a className="inline-flex items-center gap-2 text-indigo-700" href={`tel:${item.referente_cell}`}>
                              <Phone size={14} />
                              {item.referente_cell}
                            </a>
                          ) : null}
                          {item.admin_contacts ? (
                            <div className="mt-1 text-xs text-slate-500">Admin: {item.admin_contacts}</div>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-slate-600">
                        {item.previous_plan_code ? (
                          <>
                            <div>Prima: {item.previous_plan_label}</div>
                            <div className="text-xs text-slate-500">{formatDate(item.previous_plan_at)}</div>
                          </>
                        ) : (
                          <span className="text-slate-400">Nessun piano precedente</span>
                        )}
                        <div className="mt-2 text-xs text-slate-500">
                          Piano attuale dal {formatDate(item.current_plan_at)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
